import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, Plus } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { DataTable } from '@/components/DataTable'
import {
  EPK_VARIANT_LABELS,
  VARIANT_FROM_EVENT_TYPE,
  type DraftRecord,
  type EpkVariant,
} from '@/lib/epk-draft-types'
import { createEpkDraft, listEpkDrafts } from '@/lib/epk-drafts-api'
import { useAuthStore } from '@/lib/auth-store'

const ALL_VARIANTS = Object.keys(EPK_VARIANT_LABELS) as EpkVariant[]

const STATUS_COLOUR: Record<DraftRecord['status'], string> = {
  draft: 'bg-zinc-100 text-zinc-700',
  in_review: 'bg-amber-100 text-amber-800',
  authorised: 'bg-emerald-100 text-emerald-800',
  archived: 'bg-slate-100 text-slate-600',
}

export function EpkContentTab() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = role === 'admin' || role === 'manager'
  const [creating, setCreating] = useState<EpkVariant | null>(null)

  const draftsQ = useQuery<DraftRecord[], ApiClientError>({
    queryKey: ['epk-drafts'],
    queryFn: listEpkDrafts,
  })
  const drafts = draftsQ.data ?? []

  const authorisedByVariant = new Map<EpkVariant, DraftRecord>()
  for (const d of drafts) {
    if (d.status === 'authorised' && !authorisedByVariant.has(d.variant)) {
      authorisedByVariant.set(d.variant, d)
    }
  }

  async function handleCreate(variant: EpkVariant) {
    setCreating(variant)
    try {
      const seed = authorisedByVariant.get(variant)
      const created = await createEpkDraft({
        variant,
        ...(seed ? { seedFromDraftId: seed.id, label: `v${seed.version + 1}` } : {}),
      })
      await qc.invalidateQueries({ queryKey: ['epk-drafts'] })
      navigate(`/settings/epks/drafts/${created.id}`)
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Failed to create draft')
    } finally {
      setCreating(null)
    }
  }

  return (
    <div>
      <p className="mb-4 text-sm text-ovation-muted">
        Authorised EPK copy gates auto-dispatch on form submissions. Each event type maps to a
        variant — without an authorised draft, enquiries land in the inbox only.
      </p>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {ALL_VARIANTS.map((variant) => {
          const authorised = authorisedByVariant.get(variant)
          const inReview = drafts.filter(
            (d) => d.variant === variant && d.status === 'in_review',
          ).length
          return (
            <div
              key={variant}
              className="rounded-lg border border-ovation-ink/10 bg-white p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-ovation-ink">
                    {EPK_VARIANT_LABELS[variant]}
                  </div>
                  {authorised ? (
                    <div className="mt-1 text-xs text-emerald-700">
                      Authorised v{authorised.version}
                      {authorised.label ? ` · ${authorised.label}` : ''}
                    </div>
                  ) : (
                    <div className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                      <AlertTriangle className="h-3 w-3" />
                      Auto-dispatch blocked
                    </div>
                  )}
                  {inReview > 0 && (
                    <div className="mt-0.5 text-xs text-ovation-muted">
                      {inReview} in review
                    </div>
                  )}
                </div>
                {canEdit && (
                  <button
                    type="button"
                    disabled={creating === variant}
                    onClick={() => handleCreate(variant)}
                    className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs font-medium hover:bg-ovation-paper disabled:opacity-50"
                  >
                    <Plus className="inline h-3 w-3" />
                    New
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <h3 className="mb-2 font-display text-base">Event type → variant mapping</h3>
      <div className="mb-6 overflow-x-auto rounded-lg border border-ovation-ink/10 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ovation-ink/10 text-left text-xs uppercase tracking-wider text-ovation-muted">
              <th className="px-4 py-2">Form event type</th>
              <th className="px-4 py-2">EPK variant</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(VARIANT_FROM_EVENT_TYPE).map(([eventType, variant]) => (
              <tr key={eventType} className="border-b border-ovation-ink/5">
                <td className="px-4 py-2 font-mono text-xs">{eventType}</td>
                <td className="px-4 py-2">{EPK_VARIANT_LABELS[variant]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="mb-2 font-display text-base">All drafts</h3>
      <AsyncBoundary isLoading={draftsQ.isLoading} error={draftsQ.error}>
        <DataTable
          rows={drafts}
          rowKey={(r) => r.id}
          emptyMessage="No EPK drafts yet. Create one per variant to unlock auto-dispatch."
          columns={[
            {
              key: 'variant',
              header: 'Variant',
              render: (r) => EPK_VARIANT_LABELS[r.variant],
            },
            {
              key: 'version',
              header: 'Ver',
              align: 'right',
              render: (r) => `v${r.version}`,
            },
            {
              key: 'status',
              header: 'Status',
              render: (r) => (
                <span
                  className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_COLOUR[r.status]}`}
                >
                  {r.status.replace(/_/g, ' ')}
                </span>
              ),
            },
            {
              key: 'label',
              header: 'Label',
              render: (r) => r.label ?? '—',
            },
            {
              key: 'updated',
              header: 'Updated',
              render: (r) => new Date(r.updatedAt).toLocaleDateString(),
            },
            {
              key: 'actions',
              header: '',
              render: (r) => (
                <Link
                  to={`/settings/epks/drafts/${r.id}`}
                  className="text-xs font-medium text-ovation-ink underline"
                >
                  Open
                </Link>
              ),
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}
